/* eslint-disable react-hooks/exhaustive-deps */
import { type ExternalFile } from '@/lib/types';
import useRandomizedId from '@/lib/utils/useRandomizedId';
import React, { useContext, useEffect, useState } from 'react';
import AddFileButton from './AddFileButton';
import { Divider } from '@mui/material';
import GoogleDriveFilePicker from './GoogleDriveFilePicker';
import OneDriveFilePicker from './OneDriveFilePicker';
import IsGoogleDrivePickerOpenContext from '../../utils/IsGoogleDrivePickerOpenContext';
import ConnectedAccountsSettings from './ConnectedAccountsSettings';
import useAggregatedLoadingState from './useAggregatedLoadingState';
import useAggregatedErrorState from './useAggregatedErrorState';
import UploadOptionsMenu from './UploadOptionsMenu';
import UploadOptionsMenuItem from './UploadOptionsMenuItem';
import ImageUpload from './ImageUpload';
import { SavingState } from '../getSavingState';
import MenuWithLazyPersistedMounting from '@/components/Menu/MenuWithLazyPersistedMounting';

type AddFilesControlsProps = {
  savingState: SavingState;
  onUploadImages: (imageFiles: File[]) => Promise<void>;
  onAddExternalFiles: (externalFiles: ExternalFile[]) => void;
  disabled?: boolean;
};

const AddFilesControls = ({
  savingState,
  onUploadImages,
  onAddExternalFiles,
  disabled,
}: AddFilesControlsProps) => {
  const addButtonId = useRandomizedId('add-file-button');
  const menuId = useRandomizedId('file-options-menu');

  const [menuAnchorElement, setMenuAnchorElement] =
    useState<HTMLButtonElement | null>(null);
  const menuOpen = menuAnchorElement !== null;

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>): void => {
    setMenuAnchorElement(e.currentTarget);
  };

  const closeAllMenus = (): void => {
    closeUploadOptionsMenu();
    setMenuAnchorElement(null);
  };

  const uploadOptionsMenuItemId = useRandomizedId('upload-options-menu-item');
  const uploadOptionsMenuId = useRandomizedId('upload-options-menu');

  const [uploadOptionsMenuAnchorElement, setUploadOptionsMenuAnchorElement] =
    useState<HTMLLIElement | null>(null);
  const uploadOptionsMenuOpen = uploadOptionsMenuAnchorElement !== null;

  const openUploadOptionsMenu = (
    e: React.MouseEvent<HTMLLIElement> | React.KeyboardEvent<HTMLLIElement>,
  ): void => {
    setUploadOptionsMenuAnchorElement(e.currentTarget);
  };

  const closeUploadOptionsMenu = (): void => {
    setUploadOptionsMenuAnchorElement(null);
  };

  const [isGoogleDriveFilePickerOpen] = useContext(
    IsGoogleDrivePickerOpenContext,
  );

  const [isMenuForciblyUnmounted, setIsMenuForciblyUnmounted] = useState(false);

  useEffect(() => {
    if (isGoogleDriveFilePickerOpen && menuOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      closeAllMenus();
    }

    // We need to forcibly unmount the menu briefly to fix focus fighting
    // issues that otherwise occur after the Google Drive picker closes
    if (!isGoogleDriveFilePickerOpen) {
      setIsMenuForciblyUnmounted(true);

      setTimeout(() => {
        setIsMenuForciblyUnmounted(false);
      }, 10);
    }
  }, [isGoogleDriveFilePickerOpen]);

  const [
    isAddButtonLoading,
    registerAddButtonLoadStart,
    registerAddButtonLoadEnd,
  ] = useAggregatedLoadingState();

  const [addButtonHasError, registerAddButtonError, removeAddButtonError] =
    useAggregatedErrorState();

  const [
    addButtonHasWarning,
    registerAddButtonWarning,
    removeAddButtonWarning,
  ] = useAggregatedErrorState();

  const [
    isUploadOptionsLoading,
    registerUploadOptionsLoadStart,
    registerUploadOptionsLoadEnd,
  ] = useAggregatedLoadingState();

  const [
    uploadOptionsHasError,
    registerUploadOptionsError,
    removeUploadOptionsError,
  ] = useAggregatedErrorState();

  const handlePickFiles = (pickedFiles: ExternalFile[]): void => {
    closeAllMenus();

    onAddExternalFiles(pickedFiles);
  };

  const handleAttachUploadedFile = (uploadedFile: ExternalFile): void => {
    closeAllMenus();

    onAddExternalFiles([uploadedFile]);
  };

  const handleUploadImages = (imageFiles: File[]): Promise<void> => {
    closeAllMenus();

    return onUploadImages(imageFiles);
  };

  return (
    <>
      <AddFileButton
        id={addButtonId}
        onClick={openMenu}
        savingState={savingState}
        loadingPartial={
          savingState !== SavingState.NOT_SAVING || isAddButtonLoading
        }
        hasError={addButtonHasError}
        hasWarning={addButtonHasWarning}
        disabled={disabled}
        aria-controls={menuOpen ? menuId : undefined}
        aria-expanded={menuOpen ? 'true' : undefined}
        aria-haspopup
      />
      {!isMenuForciblyUnmounted && (
        <>
          <MenuWithLazyPersistedMounting
            id={menuId}
            labelledById={addButtonId}
            anchorEl={menuAnchorElement}
            open={menuOpen}
            onClose={closeAllMenus}
          >
            <GoogleDriveFilePicker
              onPickFiles={handlePickFiles}
              onLoadStart={registerAddButtonLoadStart}
              onLoadEnd={registerAddButtonLoadEnd}
              onRegisterError={registerAddButtonError}
              onRemoveError={removeAddButtonError}
            />
            <OneDriveFilePicker
              onPickFiles={handlePickFiles}
              onLoadStart={registerAddButtonLoadStart}
              onLoadEnd={registerAddButtonLoadEnd}
            />
            <Divider />
            <UploadOptionsMenuItem
              id={uploadOptionsMenuItemId}
              loading={isUploadOptionsLoading}
              hasError={uploadOptionsHasError}
              uploadOptionsMenuId={uploadOptionsMenuId}
              uploadOptionsMenuOpen={uploadOptionsMenuOpen}
              onOpenUploadOptionsMenu={openUploadOptionsMenu}
            />
            <ImageUpload
              onUploadImages={handleUploadImages}
              onLoadStart={registerAddButtonLoadStart}
              onLoadEnd={registerAddButtonLoadEnd}
              onRegisterError={registerAddButtonError}
              onRemoveError={removeAddButtonError}
              onRegisterWarning={registerAddButtonWarning}
              onRemoveWarning={removeAddButtonWarning}
            />
            <Divider />
            <ConnectedAccountsSettings />
          </MenuWithLazyPersistedMounting>
          <UploadOptionsMenu
            id={uploadOptionsMenuId}
            open={uploadOptionsMenuOpen}
            anchorEl={uploadOptionsMenuAnchorElement}
            onClose={closeUploadOptionsMenu}
            parentMenuItemId={uploadOptionsMenuItemId}
            onAttachUploadedFile={handleAttachUploadedFile}
            onRegisterAddButtonLoadStart={registerAddButtonLoadStart}
            onRegisterAddButtonLoadEnd={registerAddButtonLoadEnd}
            onRegisterAddButtonError={registerAddButtonError}
            onRemoveAddButtonError={removeAddButtonError}
            onRegisterUploadOptionsLoadStart={registerUploadOptionsLoadStart}
            onRegisterUploadOptionsLoadEnd={registerUploadOptionsLoadEnd}
            onRegisterUploadOptionsError={registerUploadOptionsError}
            onRemoveUploadOptionsError={removeUploadOptionsError}
          />
        </>
      )}
    </>
  );
};

export default AddFilesControls;
